/**
 * Organizes the activity menu and activity data into pairs.
 * This is used for presenting the activities in the summary view.
 * Articles that share the same article group are the same activity
 * what differs is the number of units booked, e.g the number of tables.
 */
import { Article } from "types/article";
import { BookingMenu, MenuStatus } from "types/menu";
import { PaymentCalculationRule, PaymentRule } from "types/payment";
import { WebBooking } from "types/web-booking";
import { WebRule } from "types/web-rule";
import { getPaymentCalculationTotal } from "utils/payment-utils";

/**
 * The price per resource is based on the webrule attatched to it and can be calculated in different ways depending on the calculation rule.
 * This function is a hack that simplifies the interface so we can provide the quantity instead of an article list.
 */
const getTotalPriceBasedOnRule = (rule: WebRule, guests: number, resourceQuantity: number) => {
    return getPaymentCalculationTotal(rule, guests, Array.from({ length: resourceQuantity }));
};

const getActivities = (
    reservation: WebBooking,
    rules: WebRule[] | null,
    sectionNameMap?: Map<number, { id: number; name: string }>
) => {
    if (!reservation || !reservation.articles) return [];

    return reservation.articles.reduce((acc: ActivityMenuPair[], activity) => {
        const activityRule = rules?.find((rule) => rule.id === activity.ruleId) || undefined;
        const alreadyAdded = acc.find((addedActivity) => addedActivity.ruleId === activity.ruleId);
        const isNoShowFee = activityRule?.paymentRule === PaymentRule.NoShowFee;
        /**
         * If the activity is already added we update the quantity and recalculate the price.
         * This is to avoid presenting duplicated menu items in the summary
         */
        if (alreadyAdded) {
            alreadyAdded.quantity += activity.quantity;
            // Recalculate the price on the new quantity.
            const totalCost = activityRule
                ? getTotalPriceBasedOnRule(activityRule, reservation.guests, alreadyAdded.quantity)
                : 0;

            if (isNoShowFee) {
                alreadyAdded.noShowFee = totalCost;
            } else {
                alreadyAdded.resourceTotalPrice = totalCost;
            }
            return acc;
        }

        const menu = reservation?.menus.find((menu) => menu.publicId === activity.menuGuid);

        // In experiences, all activities share the same menuGuid on the article,
        // but only activities whose rule has a menuGuid should show menu items.
        // For standalone (non-experience) activities, always include the menu.
        const hasMenuConnected = !activityRule?.experienceId || !!activityRule?.menuGuid;

        const menuTotalPrice =
            hasMenuConnected && menu?.groups
                ? menu.groups.reduce((menuPrice, group) => {
                      const groupPrice = group.items
                          ? group.items
                                .map((item) => item.price * item.quantity)
                                .reduce((a, b) => a + b, 0)
                          : 0;
                      return menuPrice + groupPrice;
                  }, 0)
                : 0;

        const activityCost = activityRule
            ? getTotalPriceBasedOnRule(activityRule, reservation.guests, activity.quantity)
            : 0;
        const confirmedMenu = menu?.status === MenuStatus.Confirmed ? menu : undefined;
        const sectionName = sectionNameMap?.get(activity.ruleId)?.name || activity.articleGroupName;

        const activityMenuPairs = {
            ...activity,
            ...confirmedMenu,
            groups: confirmedMenu && hasMenuConnected ? confirmedMenu.groups : undefined,
            paymentCalculationRule: activityRule?.paymentCalculationRule,
            rulePaymentAmount: activityRule?.paymentAmount,
            menuPrice: menuTotalPrice,
            resourceTotalPrice: isNoShowFee ? 0 : activityCost,
            noShowFee: isNoShowFee ? activityCost : 0,
            sectionName,
            ruleId: activityRule?.id ?? 0,
            paymentRule: activityRule?.paymentRule,
            rule: activityRule,
        };

        // Sort it in order of start time
        const index = acc.findIndex((pair) => pair.start.unix() > activity.start.unix());
        if (index === -1) acc.push(activityMenuPairs);
        else acc.splice(index, 0, activityMenuPairs);

        return acc;
    }, []);
};

export type ActivityMenuPair = Article &
    Partial<BookingMenu> & {
        resourceTotalPrice: number;
        menuPrice: number;
        noShowFee: number;
        paymentCalculationRule?: PaymentCalculationRule;
        rulePaymentAmount?: number;
        sectionName?: string;
        ruleId?: number;
        paymentRule?: PaymentRule;
        rule?: WebRule;
    };
export default getActivities;
